/**
 * Portal chrome (ADR-0010 P7 B+C · S9) — the frame around every portal page,
 * modelled on the Tray **admin** shell. Two layouts, both branded from the app's
 * `portal_config` (applied as CSS variables on the shell root so the portal
 * looks distinct from the operator UI without leaking the operator theme):
 *
 * - **standard** — a brandable left **rail**, a neutral **topbar** (page title +
 *   account menu) and a slim **footer**.
 * - **app** — a Tray-admin / Asana-style console: the same collapsible rail
 *   (with its OWN collapse toggle + account menu, since there's no header) and
 *   the page floating inside a **rounded content card** on the page background.
 *   NO header band, no footer — cleaner and more "app-like".
 *
 * **App-frame scrolling**: the shell is pinned to the viewport
 * (`h-screen overflow-hidden`) — chrome stays put and ONLY the content scrolls
 * (in `app`, the card's inner pane owns the scroll). Builder preview
 * (`embedded`) fills its canvas the same way.
 *
 * **Responsive (container-query driven)**: the rail↔drawer swap keys off the
 * shell's OWN width via `@container/shell` (`@[768px]/shell:` variants), NOT the
 * viewport — so the SAME component mirrors the real portal inside the builder's
 * device-width preview box. Below 768px the rail is replaced by a drawer opened
 * from a hamburger (in `standard` it's in the header; in `app` it's a slim
 * mobile-only bar). The drawer is `absolute inset-0` (contained to the shell
 * root, which is `relative`).
 *
 * Why it reads like the admin: the rail is the single branded surface, nav tiles
 * lift on a `currentColor` tint (works on light OR saturated rails), and
 * rail/header text **auto-contrasts** against the chosen background when the
 * operator didn't pick an explicit text colour. `show_nav=false` hides the rail
 * entirely (single-page portals). The portal is LIGHT-only (`tray-light`).
 */
import { useQuery } from "@tanstack/react-query";
import {
  ChevronDown,
  ExternalLink,
  LogOut,
  Menu,
  PanelLeftClose,
  PanelLeftOpen,
  UserCog,
  X,
} from "lucide-react";
import { type CSSProperties, type ReactNode, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { cn } from "@/lib/cn";
import { type PortalNavItem, portalApi } from "@/lib/api-portal";
import { navIcon } from "@/lib/portalNavIcons";
import {
  parentAccountPath,
  portalAssistantPath,
  portalPagePath,
  type PortalUser,
} from "@/lib/portalAuth";

import { AppSwitcher } from "./AppSwitcher";
import { InterfaceSwitcher } from "./InterfaceSwitcher";
import { NotificationBell } from "./NotificationBell";
import { VoiceAssistant } from "./VoiceAssistant";
import { ChatAssistant } from "./ChatAssistant";
import { getLayout, themeStyle } from "@/lib/portalLayouts";

/** Up-to-two-letter initials from a user's name or email, for the avatar. */
function initials(user: PortalUser): string {
  const src = (user.full_name || user.email || "?").trim();
  const parts = src.split(/[\s@._-]+/).filter(Boolean);
  const letters = (parts.length >= 2 ? parts[0][0] + parts[1][0] : src.slice(0, 2)) || "?";
  return letters.toUpperCase();
}

/**
 * Pick a readable text colour (near-black / white) for a solid background.
 * Used to default rail/header text when the operator branded the surface but
 * not its text — relative luminance per WCAG, threshold tuned by eye.
 * Returns `undefined` for a non-hex value so callers fall back to the token.
 */
function readableOn(bg: string | undefined): string | undefined {
  if (!bg) return undefined;
  const m = /^#?([0-9a-fA-F]{6})$/.exec(bg.trim());
  if (!m) return undefined;
  const n = parseInt(m[1], 16);
  const [r, g, b] = [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  const lum = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
  return lum > 0.6 ? "#0f172a" : "#ffffff";
}

/** Avatar + name → a dropdown with Sign-out. Reused by the standard header
 *  (`placement="header"`) and the app-layout rail bottom (`placement="rail"`),
 *  which positions the panel above the trigger and shows a full name/email row.
 *  Its own open state so each placement is independent. */
function AccountMenu({
  user,
  onSignOut,
  placement,
  collapsed,
}: {
  user: PortalUser;
  onSignOut: () => void;
  placement: "header" | "rail";
  collapsed?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const rail = placement === "rail";
  // Translucent currentColor so it reads on any branded surface.
  const avatarStyle: CSSProperties = {
    background: "color-mix(in srgb, currentColor 18%, transparent)",
  };
  return (
    <div className={cn("relative", rail && "w-full")}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        className={cn(
          "flex items-center gap-2.5 transition-colors hover:bg-foreground/10 focus-visible:bg-foreground/10 focus-visible:outline-none",
          rail
            ? cn("w-full rounded-[var(--portal-radius)] p-2", collapsed && "justify-center p-0")
            : "rounded-full py-1 pl-1 pr-2",
        )}
      >
        <span
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold"
          style={avatarStyle}
        >
          {initials(user)}
        </span>
        {rail
          ? !collapsed && (
              <>
                <div className="min-w-0 flex-1 text-left leading-tight">
                  <div className="truncate text-xs font-medium">{user.full_name || user.email}</div>
                  {user.full_name && <div className="truncate text-[11px] opacity-60">{user.email}</div>}
                </div>
                <ChevronDown className="h-4 w-4 shrink-0 opacity-60" strokeWidth={1.5} />
              </>
            )
          : (
              <>
                <span className="hidden max-w-[140px] truncate font-medium @[640px]/shell:inline">
                  {user.full_name || user.email}
                </span>
                <ChevronDown className="hidden h-4 w-4 opacity-60 @[640px]/shell:inline" strokeWidth={1.5} />
              </>
            )}
      </button>
      {open && (
        <>
          {/* Click-away layer. */}
          <button
            type="button"
            aria-hidden
            tabIndex={-1}
            className="fixed inset-0 z-40 cursor-default"
            onClick={() => setOpen(false)}
          />
          <div
            role="menu"
            className={cn(
              "portal-pop absolute z-50 w-56 overflow-hidden rounded-[var(--portal-radius)] border border-border bg-surface text-foreground shadow-lg",
              rail ? "bottom-full left-0 mb-1.5" : "right-0 top-full mt-1.5",
            )}
          >
            <div className="border-b border-border px-3 py-2.5">
              <div className="truncate text-sm font-medium">{user.full_name || user.email}</div>
              {user.full_name && <div className="truncate text-xs text-muted-foreground">{user.email}</div>}
            </div>
            <Link
              to={parentAccountPath()}
              role="menuitem"
              onClick={() => setOpen(false)}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-muted focus-visible:bg-muted focus-visible:outline-none"
            >
              <UserCog className="h-4 w-4" strokeWidth={1.5} />
              Account
            </Link>
            <button
              type="button"
              role="menuitem"
              className="flex w-full items-center gap-2 border-t border-border px-3 py-2 text-left text-sm text-danger transition-colors hover:bg-muted focus-visible:bg-muted focus-visible:outline-none"
              onClick={() => {
                setOpen(false);
                onSignOut();
              }}
            >
              <LogOut className="h-4 w-4" strokeWidth={1.5} />
              Sign out
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export function PortalShell({
  appSlug,
  activePageSlug,
  user,
  onSignOut,
  children,
  embedded = false,
}: {
  appSlug: string;
  activePageSlug?: string;
  user: PortalUser | null;
  onSignOut: () => void;
  children: ReactNode;
  /** Builder preview: size to content instead of the full viewport, so the
   *  same shell renders inside the page-builder canvas (max reuse). */
  embedded?: boolean;
}) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  // Avatar chip — translucent currentColor so it reads on any branded surface.
  const avatarStyle: CSSProperties = {
    background: "color-mix(in srgb, currentColor 18%, transparent)",
  };

  const brandingQ = useQuery({
    queryKey: ["portal-branding", appSlug],
    queryFn: () => portalApi.getBranding(appSlug),
    staleTime: 5 * 60_000,
  });
  const navQ = useQuery({
    queryKey: ["portal-nav", appSlug],
    queryFn: () => portalApi.getNav(appSlug),
    staleTime: 60_000,
  });

  const branding = brandingQ.data;
  const theme = branding?.theme ?? {};
  const nav = navQ.data ?? [];
  const showNav = (branding?.show_nav ?? true) && nav.length > 0;
  const title = branding?.app_name ?? appSlug;
  // The "app" layout drops the header band + footer and floats the content in a
  // rounded card; the rail hosts its own collapse toggle + account menu.
  const isApp = getLayout(branding?.layout).id === "app";
  // Pages can live at the top level OR inside a group, so flatten to resolve
  // the active page's title for the mobile header.
  const flatPages = nav.flatMap((n) => (n.kind === "group" ? (n.children ?? []) : [n]));
  const activePage = flatPages.find((p) => p.kind === "page" && p.slug === activePageSlug);
  const pageTitle = activePage?.label ?? title;

  // Auto-contrast: when a surface is branded but its *_text slot is empty,
  // derive a readable text colour from the background luminance.
  const railText = theme.sidebar_text || readableOn(theme.sidebar);
  const headerText = theme.header_text || readableOn(theme.header);

  // The rail is the dark "void" plane (its own `sidebar` colour). In `app` the
  // content panel then LIFTS off it (a brighter `--surface` with a rounded
  // top-left + left/top seam) — the Tray-admin "lifted page" (see AppShell).
  const railStyle: CSSProperties = {
    background: theme.sidebar || "hsl(var(--surface))",
    color: railText || "hsl(var(--foreground))",
  };

  const logo = branding?.logo_url ? (
    <img src={branding.logo_url} alt={title} className="h-7 max-w-[150px] object-contain" />
  ) : (
    <span className="truncate text-sm font-semibold tracking-tight">{title}</span>
  );

  // One nav node → its rendered tile. A `page` is an internal Link, a `link`
  // is an external anchor (new tab), and a `group` is a header + its children.
  const renderNode = (
    node: PortalNavItem,
    opts: { collapsed?: boolean; onNavigate?: () => void },
    key: string,
  ) => {
    if (node.kind === "group") {
      // Groups never collapse to icon-only (the label is the whole point);
      // when the rail is collapsed we just render the children flush.
      const children = (node.children ?? []).map((c, i) => renderNode(c, opts, `${key}-${i}`));
      if (opts.collapsed) return <div key={key}>{children}</div>;
      return (
        <div key={key} className="pt-2 first:pt-0">
          <div className="px-2.5 pb-1 text-[11px] font-semibold uppercase tracking-wide opacity-60">
            {node.label}
          </div>
          <div className="space-y-0.5">{children}</div>
        </div>
      );
    }

    const Icon = navIcon(node);
    const tile = (extra: string, active: boolean) =>
      cn(
        "portal-nav relative",
        active ? "portal-nav--active" : "portal-nav--idle",
        opts.collapsed && "justify-center px-0",
        extra,
      );

    if (node.kind === "link") {
      return (
        <a
          key={key}
          href={node.url ?? "#"}
          target="_blank"
          rel="noreferrer"
          title={node.label}
          // Inert in the builder preview (don't navigate away from the editor).
          onClick={(e) => {
            if (embedded) e.preventDefault();
            opts.onNavigate?.();
          }}
          className={tile("", false)}
        >
          <Icon className="h-4 w-4 shrink-0" strokeWidth={1.5} />
          {!opts.collapsed && (
            <>
              <span className="truncate">{node.label}</span>
              <ExternalLink className="ml-auto h-3 w-3 shrink-0 opacity-50" strokeWidth={1.5} />
            </>
          )}
        </a>
      );
    }

    // page
    const active = node.slug === activePageSlug;
    return (
      <Link
        key={key}
        to={portalPagePath(appSlug, node.slug ?? "")}
        title={node.label}
        // In the builder (embedded) the shell is interactive ONLY so the
        // sidebar can collapse / the drawer can open — actual navigation is
        // inert (don't leave the editor); the drawer still closes on tap.
        onClick={(e) => {
          if (embedded) e.preventDefault();
          opts.onNavigate?.();
        }}
        className={tile("", active)}
      >
        {/* Active indicator — a short accent bar on the left edge. */}
        {active && !opts.collapsed && (
          <span className="absolute inset-y-1.5 left-0 w-0.5 rounded-r-full bg-current" />
        )}
        <Icon className="h-4 w-4 shrink-0" strokeWidth={1.5} />
        {!opts.collapsed && <span className="truncate">{node.label}</span>}
      </Link>
    );
  };

  // Nav tiles — reused by the desktop rail and the mobile drawer. `onNavigate`
  // lets the drawer close itself when a link is tapped.
  const navLinks = (opts: { collapsed?: boolean; onNavigate?: () => void } = {}) =>
    nav.map((node, i) => renderNode(node, opts, `nav-${i}`));

  return (
    <div
      className={cn(
        // `@container/shell` makes the rail↔drawer swap respond to the shell's
        // OWN width (container query), not the viewport. `relative` lets the
        // mobile drawer be `absolute`-contained to the shell. `tray-light` pins
        // the portal to the light token set (never inherits admin dark mode).
        "tray-light @container/shell relative flex overflow-hidden",
        // `h-screen` (100vh) is the LARGE viewport on iOS Safari (URL bar
        // collapsed) — taller than the visible area, so the whole shell
        // page-scrolls and drags the "fixed" header off-screen on mobile. Pin to
        // `100dvh` (dynamic viewport) where supported so the root == the visible
        // viewport and ONLY `<main>` scrolls; `h-screen` stays the fallback.
        embedded ? "h-full" : "h-screen supports-[height:100dvh]:h-[100dvh]",
      )}
      style={{
        ...themeStyle(branding?.layout, theme),
        // In `app` the page void IS the sidebar plane (the panel lifts off it),
        // so derive it from `sidebar` — there's no separate page-background knob.
        background: isApp
          ? theme.sidebar || "hsl(var(--surface))"
          : "var(--portal-bg, hsl(var(--bg)))",
        color: "var(--portal-text, hsl(var(--fg)))",
        // App-wide default content width — each page inherits it unless its own
        // `max_width` overrides (S8b). "full" → no cap.
        ["--portal-content-max" as string]:
          branding?.content_width === "full" ? "none" : "1024px",
        // Configurable corner radius (default 6px from globals; an app can set a
        // `radius` theme value — e.g. "0" for sharp, "12px" for rounded). Every
        // portal surface reads `rounded-[var(--portal-radius)]`.
        ...(theme.radius ? { ["--portal-radius" as string]: theme.radius } : {}),
      }}
    >
      {/* Voice assistant (ADR-0025) — only for signed-in users where the app's
          voice interface is enabled. Floating launcher + full-screen HUD. */}
      {user && branding?.voice_enabled && (
        <VoiceAssistant
          appSlug={appSlug}
          appName={title}
          sttMode={branding.voice_stt === "server" ? "server" : "browser"}
          ttsMode={branding.voice_tts === "server" ? "server" : "browser"}
          wakeWord={!!branding.voice_wake_word}
          onExpand={
            embedded || !branding.voice_standalone
              ? undefined
              : () => navigate(portalAssistantPath(appSlug, "voice"))
          }
        />
      )}

      {/* Chat assistant (ADR-0028 Block E) — the typed twin of the voice widget,
          same server brain + scope. Shifted left when voice is also on so the
          two launchers don't overlap. */}
      {user && branding?.chat_enabled && (
        <ChatAssistant
          appSlug={appSlug}
          appName={title}
          sideOffset={!!branding.voice_enabled}
          onExpand={
            embedded || !branding.chat_standalone
              ? undefined
              : () => navigate(portalAssistantPath(appSlug, "chat"))
          }
        />
      )}

      {/* Desktop rail — the single branded surface; in-flow ≥ 768px of shell
          width, scrolls its own nav. Below that it's a drawer. */}
      {showNav && (
        <aside
          className={cn(
            "relative z-10 hidden shrink-0 flex-col transition-[width] duration-150 @[768px]/shell:flex",
            // The `standard` rail casts a subtle right shadow; in `app` the
            // content panel's own border/shadow draws the seam instead.
            !isApp && "shadow-[1px_0_4px_rgba(15,23,42,0.06)]",
            collapsed ? "w-14" : "w-60",
          )}
          style={railStyle}
        >
          <div
            className={cn("flex h-14 shrink-0 items-center gap-2 px-3", collapsed && "justify-center px-0")}
            style={{ boxShadow: "inset 0 -1px 0 0 color-mix(in srgb, currentColor 12%, transparent)" }}
          >
            {collapsed
              ? branding?.logo_url
                ? <img src={branding.logo_url} alt={title} className="h-6 w-6 object-contain" />
                : <span className="text-sm font-semibold">{title.slice(0, 1).toUpperCase()}</span>
              : logo}
          </div>
          <nav className="flex flex-1 flex-col gap-0.5 overflow-y-auto p-2">
            {!collapsed && (
              <p className="px-2 pb-1 pt-1.5 text-[10px] font-semibold uppercase tracking-wider opacity-50">
                Menu
              </p>
            )}
            {navLinks({ collapsed })}
          </nav>
          {/* Static user chip at the rail bottom — only in `standard` (the `app`
              layout puts the account menu in the floating card's toolbar). */}
          {user && !isApp && (
            <div
              className={cn("flex items-center gap-2.5 p-3", collapsed && "justify-center px-0")}
              style={{ boxShadow: "inset 0 1px 0 0 color-mix(in srgb, currentColor 12%, transparent)" }}
            >
              <span
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold"
                style={avatarStyle}
              >
                {initials(user)}
              </span>
              {!collapsed && (
                <div className="min-w-0 leading-tight">
                  <div className="truncate text-xs font-medium">{user.full_name || user.email}</div>
                  {user.full_name && (
                    <div className="truncate text-[11px] opacity-60">{user.email}</div>
                  )}
                </div>
              )}
            </div>
          )}
        </aside>
      )}

      {/* Mobile drawer — full-screen, only mounted while open (so there's no
          duplicate hidden nav). Closes on backdrop / close button / link tap. */}
      {showNav && mobileOpen && (
        <div className="absolute inset-0 z-50 flex @[768px]/shell:hidden" role="dialog" aria-modal="true">
          <button
            type="button"
            aria-label="Close navigation"
            className="absolute inset-0 bg-black/50"
            onClick={() => setMobileOpen(false)}
          />
          <aside className="relative flex w-full max-w-[88%] flex-col shadow-2xl" style={railStyle}>
            <div
              className="flex h-14 shrink-0 items-center gap-2 px-3"
              style={{ boxShadow: "inset 0 -1px 0 0 color-mix(in srgb, currentColor 12%, transparent)" }}
            >
              {logo}
              <button
                type="button"
                aria-label="Close navigation"
                className="ml-auto rounded-md p-1.5 transition-colors hover:bg-foreground/10"
                onClick={() => setMobileOpen(false)}
              >
                <X className="h-5 w-5" strokeWidth={1.5} />
              </button>
            </div>
            <nav className="flex flex-1 flex-col gap-0.5 overflow-y-auto p-2">
              {navLinks({ onNavigate: () => setMobileOpen(false) })}
            </nav>
          </aside>
        </div>
      )}

      {/* Main column. In `app` this column IS the "lifted page" (Linear/Tray
          admin): a brighter `--surface` panel that rests on the darker rail/void
          — a rounded top-left corner + a left/top hairline seam + a tiny shadow,
          flush to the right/bottom edges, with a small top gap (`mt-2`). On
          mobile (no rail) it fills edge-to-edge. */}
      <div
        className={cn(
          "flex min-h-0 min-w-0 flex-1 flex-col",
          isApp &&
            "overflow-hidden bg-surface @[768px]/shell:mt-2 @[768px]/shell:rounded-tl-[var(--portal-radius)] @[768px]/shell:border-l @[768px]/shell:border-t @[768px]/shell:border-border @[768px]/shell:shadow-sm",
        )}
      >
        {isApp ? (
          <>
            {/* Toolbar inside the lifted panel (collapse/hamburger + title +
                account) — no separate full-width header band. */}
            <div className="flex h-14 shrink-0 items-center gap-3 bg-surface px-3 sm:px-5">
              {showNav && (
                <>
                  {/* Mobile: open the drawer. */}
                  <button
                    type="button"
                    aria-label="Open navigation"
                    className="-ml-1 rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-foreground/5 hover:text-foreground focus-visible:bg-foreground/5 focus-visible:outline-none @[768px]/shell:hidden"
                    onClick={() => setMobileOpen(true)}
                  >
                    <Menu className="h-5 w-5" strokeWidth={1.5} />
                  </button>
                  {/* Desktop: collapse the rail — boxed icon button, like the
                      Tray admin's top-left toggle. */}
                  <button
                    type="button"
                    aria-label={collapsed ? "Expand navigation" : "Collapse navigation"}
                    title={collapsed ? "Expand navigation" : "Collapse navigation"}
                    className="-ml-1 hidden rounded-[var(--portal-radius)] border border-border p-1.5 text-muted-foreground transition-colors hover:bg-foreground/5 hover:text-foreground focus-visible:bg-foreground/5 focus-visible:outline-none @[768px]/shell:inline-flex"
                    onClick={() => setCollapsed((c) => !c)}
                  >
                    {collapsed ? (
                      <PanelLeftOpen className="h-4 w-4" strokeWidth={1.5} />
                    ) : (
                      <PanelLeftClose className="h-4 w-4" strokeWidth={1.5} />
                    )}
                  </button>
                </>
              )}
              <span className="truncate text-sm font-semibold tracking-tight text-foreground">
                {pageTitle}
              </span>
              <div className="ml-auto flex items-center gap-2 text-sm">
                {user && !embedded && <InterfaceSwitcher appSlug={appSlug} current="web" />}
                {user && <AppSwitcher currentSlug={appSlug} />}
                {user && <NotificationBell appSlug={appSlug} embedded={embedded} />}
                {user && <AccountMenu user={user} onSignOut={onSignOut} placement="header" />}
              </div>
            </div>
            {/* The panel's inner pane owns the scroll. The page title already
                lives in the sticky toolbar above (Tray-admin style), so there's
                NO separate in-content title band — that was a redundant second
                "Deals". */}
            <main className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-4 py-6 sm:px-6">
              <div className="mx-auto" style={{ maxWidth: "var(--portal-content-max, 1024px)" }}>
                {children}
              </div>
            </main>
          </>
        ) : (
          <>
            <header
              className="z-10 flex h-14 shrink-0 items-center gap-3 border-b border-border px-4 shadow-[0_1px_3px_rgba(15,23,42,0.05)]"
              style={{ background: theme.header || "hsl(var(--surface))", color: headerText || "inherit" }}
            >
              {showNav ? (
                <>
                  {/* Mobile: open the drawer. */}
                  <button
                    type="button"
                    aria-label="Open navigation"
                    className="-ml-1 rounded-md p-1.5 transition-colors hover:bg-foreground/10 focus-visible:bg-foreground/10 focus-visible:outline-none @[768px]/shell:hidden"
                    onClick={() => setMobileOpen(true)}
                  >
                    <Menu className="h-5 w-5" strokeWidth={1.5} />
                  </button>
                  {/* Desktop: collapse the rail. */}
                  <button
                    type="button"
                    aria-label={collapsed ? "Expand navigation" : "Collapse navigation"}
                    title={collapsed ? "Expand navigation" : "Collapse navigation"}
                    className="-ml-1 hidden rounded-md p-1.5 transition-colors hover:bg-foreground/10 focus-visible:bg-foreground/10 focus-visible:outline-none @[768px]/shell:inline-flex"
                    onClick={() => setCollapsed((c) => !c)}
                  >
                    {collapsed ? (
                      <PanelLeftOpen className="h-4 w-4" strokeWidth={1.5} />
                    ) : (
                      <PanelLeftClose className="h-4 w-4" strokeWidth={1.5} />
                    )}
                  </button>
                </>
              ) : (
                // Single-page portal: the brand lives in the topbar instead.
                <div className="flex items-center">{logo}</div>
              )}

              {showNav && (
                <span className="truncate text-sm font-semibold tracking-tight">{pageTitle}</span>
              )}

              <div className="ml-auto flex items-center gap-2 text-sm">
                {user && !embedded && <InterfaceSwitcher appSlug={appSlug} current="web" />}
                {user && <AppSwitcher currentSlug={appSlug} />}
                {user && <NotificationBell appSlug={appSlug} embedded={embedded} />}
                {user && <AccountMenu user={user} onSignOut={onSignOut} placement="header" />}
              </div>
            </header>

            {/* Page content — the ONLY scroll region. */}
            <main
              className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-4 py-6 sm:px-6"
              style={{ background: "var(--portal-bg, hsl(var(--muted)))" }}
            >
              {/* Page-title band — anchors the content with a clear H1 + hairline. */}
              {showNav && activePage && (
                <div className="mb-6 border-b border-border pb-4">
                  <h1 className="text-xl font-semibold tracking-tight text-foreground">
                    {activePage.label}
                  </h1>
                </div>
              )}
              {children}
            </main>

            <footer className="shrink-0 border-t border-border bg-surface px-4 py-2.5 text-center text-xs text-muted-foreground">
              {title}
            </footer>
          </>
        )}
      </div>
    </div>
  );
}
